/* ═══════════════════════════════════════════════════════════════════
   RFW SCORE SYSTEM — Mock Data Central
   BLOQUE 1 DE 10 — Núcleo de Competencia
   ═══════════════════════════════════════════════════════════════════ */

/* ─── Score Trend ─── */
export type ScoreTrend = 'exploding' | 'trending' | 'growing' | 'active' | 'stable' | 'declining';

export const trendConfig: Record<ScoreTrend, { label: string; icon: string; emoji: string; color: string; bg: string }> = {
  exploding:  { label: 'En Ascenso',      icon: 'ri-rocket-line',       emoji: '🚀', color: 'text-rose-600',    bg: 'bg-rose-50' },
  trending:   { label: 'Tendencia Alta',   icon: 'ri-fire-line',        emoji: '🔥', color: 'text-orange-600',  bg: 'bg-orange-50' },
  growing:    { label: 'Creciendo',        icon: 'ri-line-chart-line',  emoji: '📈', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  active:     { label: 'Activa',           icon: 'ri-flashlight-line',  emoji: '⚡', color: 'text-amber-600',   bg: 'bg-amber-50' },
  stable:     { label: 'Estable',          icon: 'ri-subtract-line',    emoji: '😴', color: 'text-stone-500',   bg: 'bg-stone-50' },
  declining:  { label: 'Bajando',          icon: 'ri-arrow-down-line',  emoji: '📉', color: 'text-red-500',     bg: 'bg-red-50' },
};

/* ─── Score Breakdown Categories ─── */
export type ScoreCategory =
  | 'publicaciones'
  | 'eventos'
  | 'concursos'
  | 'comentarios'
  | 'likes'
  | 'compartidos'
  | 'seguidores'
  | 'clubes'
  | 'locutores'
  | 'programas'
  | 'saludos'
  | 'canciones'
  | 'encuestas'
  | 'participacion_concursos'
  | 'ganador_concurso'
  | 'invitados'
  | 'verificacion'
  | 'horas_radio'
  | 'transmisiones'
  | 'perfil_creado'
  | 'asistir_evento'
  | 'invitar_amigo'
  | 'actividad_miembro'
  | 'victoria_competencia'
  | 'menciones'
  | 'escuchas_activas';

export const scoreCategoryLabels: Record<ScoreCategory, string> = {
  publicaciones: 'Publicaciones',
  eventos: 'Eventos',
  concursos: 'Concursos',
  comentarios: 'Comentarios',
  likes: 'Likes',
  compartidos: 'Compartidos',
  seguidores: 'Seguidores',
  clubes: 'Clubes',
  locutores: 'Locutores',
  programas: 'Programas',
  saludos: 'Saludos',
  canciones: 'Canciones',
  encuestas: 'Encuestas',
  participacion_concursos: 'Participación Concursos',
  ganador_concurso: 'Ganador Concurso',
  invitados: 'Invitados',
  verificacion: 'Verificación',
  horas_radio: 'Horas al Aire',
  transmisiones: 'Transmisiones',
  perfil_creado: 'Perfil Creado',
  asistir_evento: 'Asistir Evento',
  invitar_amigo: 'Invitar Amigo',
  actividad_miembro: 'Actividad Miembro',
  victoria_competencia: 'Victoria Competencia',
  menciones: 'Menciones',
  escuchas_activas: 'Escuchas Activas',
};

export const scoreCategoryIcons: Record<ScoreCategory, string> = {
  publicaciones: 'ri-article-line',
  eventos: 'ri-calendar-event-line',
  concursos: 'ri-trophy-line',
  comentarios: 'ri-chat-1-line',
  likes: 'ri-heart-line',
  compartidos: 'ri-share-forward-line',
  seguidores: 'ri-user-heart-line',
  clubes: 'ri-vip-crown-line',
  locutores: 'ri-mic-line',
  programas: 'ri-calendar-check-line',
  saludos: 'ri-hand-heart-line',
  canciones: 'ri-music-2-line',
  encuestas: 'ri-bar-chart-line',
  participacion_concursos: 'ri-user-star-line',
  ganador_concurso: 'ri-medal-line',
  invitados: 'ri-user-add-line',
  verificacion: 'ri-verified-badge-line',
  horas_radio: 'ri-time-line',
  transmisiones: 'ri-broadcast-line',
  perfil_creado: 'ri-user-smile-line',
  asistir_evento: 'ri-map-pin-line',
  invitar_amigo: 'ri-user-shared-line',
  actividad_miembro: 'ri-group-line',
  victoria_competencia: 'ri-vip-diamond-line',
  menciones: 'ri-at-line',
  escuchas_activas: 'ri-headphone-line',
};

/* ─── Score Data Interface ─── */
export interface ScoreBreakdown {
  category: ScoreCategory;
  points: number;
  count: number;
}

export interface RfwScoreData {
  entityId: number;
  entityName: string;
  entityType: 'radio' | 'programa' | 'locutor' | 'club' | 'oyente';
  currentScore: number;
  weeklyScore: number;
  monthlyScore: number;
  allTimeScore: number;
  scoreChange: number;
  rankingPosition: number;
  rankingPreviousPosition: number;
  lastScoreUpdate: string;
  trend: ScoreTrend;
  breakdown: ScoreBreakdown[];
  history: ScoreHistoryEntry[];
}

export interface ScoreHistoryEntry {
  date: string;
  score: number;
  change: number;
}

/* ─── Top-Level Ranking ─── */
export interface RfwRankingEntry {
  position: number;
  previousPosition: number;
  entityType: 'radio' | 'programa' | 'locutor' | 'club' | 'oyente';
  entityId: number;
  entityName: string;
  entitySubtitle: string;
  thumbnailUrl: string;
  currentScore: number;
  scoreChange: number;
  trend: ScoreTrend;
  growthPercent: number;
}

/* ═══════════════════════════════════════════════════════════════════
   RADIO SCORES — 12 radios con datos realistas
   ═══════════════════════════════════════════════════════════════════ */
export const radioScores: RfwScoreData[] = [
  {
    entityId: 12,
    entityName: 'Banda Radio MX',
    entityType: 'radio',
    currentScore: 28450,
    weeklyScore: 3420,
    monthlyScore: 12800,
    allTimeScore: 28450,
    scoreChange: 420,
    rankingPosition: 1,
    rankingPreviousPosition: 2,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'publicaciones', points: 2450, count: 490 },
      { category: 'eventos', points: 1800, count: 120 },
      { category: 'concursos', points: 2250, count: 90 },
      { category: 'comentarios', points: 3120, count: 3120 },
      { category: 'likes', points: 5600, count: 5600 },
      { category: 'compartidos', points: 4200, count: 840 },
      { category: 'seguidores', points: 4800, count: 480 },
      { category: 'clubes', points: 1500, count: 100 },
      { category: 'locutores', points: 400, count: 40 },
      { category: 'programas', points: 300, count: 30 },
      { category: 'saludos', points: 520, count: 260 },
      { category: 'canciones', points: 480, count: 240 },
      { category: 'encuestas', points: 340, count: 170 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 590, count: 590 },
    ],
    history: [
      { date: '2026-06-15', score: 28030, change: 380 },
      { date: '2026-06-16', score: 28150, change: 120 },
      { date: '2026-06-17', score: 28280, change: 130 },
      { date: '2026-06-18', score: 28310, change: 30 },
      { date: '2026-06-19', score: 28390, change: 80 },
      { date: '2026-06-20', score: 28500, change: 110 },
      { date: '2026-06-21', score: 28450, change: -50 },
    ],
  },
  {
    entityId: 3,
    entityName: 'La Mexicana FM',
    entityType: 'radio',
    currentScore: 27600,
    weeklyScore: 2980,
    monthlyScore: 11200,
    allTimeScore: 27600,
    scoreChange: 180,
    rankingPosition: 2,
    rankingPreviousPosition: 1,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'trending',
    breakdown: [
      { category: 'publicaciones', points: 2100, count: 420 },
      { category: 'eventos', points: 2250, count: 150 },
      { category: 'concursos', points: 1950, count: 78 },
      { category: 'comentarios', points: 2900, count: 2900 },
      { category: 'likes', points: 5100, count: 5100 },
      { category: 'compartidos', points: 3800, count: 760 },
      { category: 'seguidores', points: 5200, count: 520 },
      { category: 'clubes', points: 1350, count: 90 },
      { category: 'locutores', points: 350, count: 35 },
      { category: 'programas', points: 280, count: 28 },
      { category: 'saludos', points: 480, count: 240 },
      { category: 'canciones', points: 420, count: 210 },
      { category: 'encuestas', points: 300, count: 150 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 520, count: 520 },
    ],
    history: [
      { date: '2026-06-15', score: 27420, change: 210 },
      { date: '2026-06-16', score: 27480, change: 60 },
      { date: '2026-06-17', score: 27550, change: 70 },
      { date: '2026-06-18', score: 27620, change: 70 },
      { date: '2026-06-19', score: 27590, change: -30 },
      { date: '2026-06-20', score: 27700, change: 110 },
      { date: '2026-06-21', score: 27600, change: -100 },
    ],
  },
  {
    entityId: 7,
    entityName: 'Deportes Total Radio',
    entityType: 'radio',
    currentScore: 26100,
    weeklyScore: 3150,
    monthlyScore: 10800,
    allTimeScore: 26100,
    scoreChange: 350,
    rankingPosition: 3,
    rankingPreviousPosition: 4,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'publicaciones', points: 1950, count: 390 },
      { category: 'eventos', points: 1650, count: 110 },
      { category: 'concursos', points: 1800, count: 72 },
      { category: 'comentarios', points: 3400, count: 3400 },
      { category: 'likes', points: 4800, count: 4800 },
      { category: 'compartidos', points: 3500, count: 700 },
      { category: 'seguidores', points: 4400, count: 440 },
      { category: 'clubes', points: 1200, count: 80 },
      { category: 'locutores', points: 300, count: 30 },
      { category: 'programas', points: 250, count: 25 },
      { category: 'saludos', points: 380, count: 190 },
      { category: 'canciones', points: 340, count: 170 },
      { category: 'encuestas', points: 280, count: 140 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 480, count: 480 },
      { category: 'participacion_concursos', points: 350, count: 70 },
      { category: 'ganador_concurso', points: 100, count: 10 },
    ],
    history: [
      { date: '2026-06-15', score: 25750, change: 310 },
      { date: '2026-06-16', score: 25820, change: 70 },
      { date: '2026-06-17', score: 25900, change: 80 },
      { date: '2026-06-18', score: 26050, change: 150 },
      { date: '2026-06-19', score: 26150, change: 100 },
      { date: '2026-06-20', score: 26200, change: 50 },
      { date: '2026-06-21', score: 26100, change: -100 },
    ],
  },
  {
    entityId: 5,
    entityName: 'Rock Total Radio',
    entityType: 'radio',
    currentScore: 24200,
    weeklyScore: 2800,
    monthlyScore: 9800,
    allTimeScore: 24200,
    scoreChange: -120,
    rankingPosition: 4,
    rankingPreviousPosition: 3,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'active',
    breakdown: [
      { category: 'publicaciones', points: 1800, count: 360 },
      { category: 'eventos', points: 1500, count: 100 },
      { category: 'concursos', points: 1650, count: 66 },
      { category: 'comentarios', points: 2800, count: 2800 },
      { category: 'likes', points: 4200, count: 4200 },
      { category: 'compartidos', points: 3000, count: 600 },
      { category: 'seguidores', points: 3600, count: 360 },
      { category: 'clubes', points: 1050, count: 70 },
      { category: 'locutores', points: 280, count: 28 },
      { category: 'programas', points: 220, count: 22 },
      { category: 'saludos', points: 320, count: 160 },
      { category: 'canciones', points: 280, count: 140 },
      { category: 'encuestas', points: 240, count: 120 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 460, count: 460 },
    ],
    history: [
      { date: '2026-06-15', score: 24320, change: -80 },
      { date: '2026-06-16', score: 24280, change: -40 },
      { date: '2026-06-17', score: 24250, change: -30 },
      { date: '2026-06-18', score: 24300, change: 50 },
      { date: '2026-06-19', score: 24260, change: -40 },
      { date: '2026-06-20', score: 24320, change: 60 },
      { date: '2026-06-21', score: 24200, change: -120 },
    ],
  },
  {
    entityId: 9,
    entityName: 'La Kalle 99.9',
    entityType: 'radio',
    currentScore: 22800,
    weeklyScore: 2560,
    monthlyScore: 9200,
    allTimeScore: 22800,
    scoreChange: 560,
    rankingPosition: 5,
    rankingPreviousPosition: 7,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'publicaciones', points: 1650, count: 330 },
      { category: 'eventos', points: 1350, count: 90 },
      { category: 'concursos', points: 1500, count: 60 },
      { category: 'comentarios', points: 2600, count: 2600 },
      { category: 'likes', points: 3800, count: 3800 },
      { category: 'compartidos', points: 2800, count: 560 },
      { category: 'seguidores', points: 2800, count: 280 },
      { category: 'clubes', points: 900, count: 60 },
      { category: 'locutores', points: 240, count: 24 },
      { category: 'programas', points: 200, count: 20 },
      { category: 'saludos', points: 300, count: 150 },
      { category: 'canciones', points: 260, count: 130 },
      { category: 'encuestas', points: 220, count: 110 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 430, count: 430 },
    ],
    history: [
      { date: '2026-06-15', score: 22240, change: 480 },
      { date: '2026-06-16', score: 22350, change: 110 },
      { date: '2026-06-17', score: 22480, change: 130 },
      { date: '2026-06-18', score: 22600, change: 120 },
      { date: '2026-06-19', score: 22750, change: 150 },
      { date: '2026-06-20', score: 22850, change: 100 },
      { date: '2026-06-21', score: 22800, change: -50 },
    ],
  },
  {
    entityId: 1,
    entityName: 'Radio Nueva Actitud',
    entityType: 'radio',
    currentScore: 21500,
    weeklyScore: 2300,
    monthlyScore: 8600,
    allTimeScore: 21500,
    scoreChange: -80,
    rankingPosition: 6,
    rankingPreviousPosition: 5,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'stable',
    breakdown: [
      { category: 'publicaciones', points: 1550, count: 310 },
      { category: 'eventos', points: 1200, count: 80 },
      { category: 'concursos', points: 1350, count: 54 },
      { category: 'comentarios', points: 2400, count: 2400 },
      { category: 'likes', points: 3500, count: 3500 },
      { category: 'compartidos', points: 2600, count: 520 },
      { category: 'seguidores', points: 2600, count: 260 },
      { category: 'clubes', points: 750, count: 50 },
      { category: 'locutores', points: 220, count: 22 },
      { category: 'programas', points: 180, count: 18 },
      { category: 'saludos', points: 280, count: 140 },
      { category: 'canciones', points: 240, count: 120 },
      { category: 'encuestas', points: 200, count: 100 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 410, count: 410 },
    ],
    history: [
      { date: '2026-06-15', score: 21580, change: -60 },
      { date: '2026-06-16', score: 21540, change: -40 },
      { date: '2026-06-17', score: 21560, change: 20 },
      { date: '2026-06-18', score: 21530, change: -30 },
      { date: '2026-06-19', score: 21580, change: 50 },
      { date: '2026-06-20', score: 21560, change: -20 },
      { date: '2026-06-21', score: 21500, change: -60 },
    ],
  },
  {
    entityId: 2,
    entityName: 'Radio Fe y Esperanza',
    entityType: 'radio',
    currentScore: 20800,
    weeklyScore: 1950,
    monthlyScore: 7800,
    allTimeScore: 20800,
    scoreChange: 90,
    rankingPosition: 7,
    rankingPreviousPosition: 8,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'publicaciones', points: 1350, count: 270 },
      { category: 'eventos', points: 1050, count: 70 },
      { category: 'concursos', points: 1200, count: 48 },
      { category: 'comentarios', points: 2100, count: 2100 },
      { category: 'likes', points: 3200, count: 3200 },
      { category: 'compartidos', points: 2200, count: 440 },
      { category: 'seguidores', points: 3000, count: 300 },
      { category: 'clubes', points: 600, count: 40 },
      { category: 'locutores', points: 200, count: 20 },
      { category: 'programas', points: 160, count: 16 },
      { category: 'saludos', points: 260, count: 130 },
      { category: 'canciones', points: 220, count: 110 },
      { category: 'encuestas', points: 180, count: 90 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 380, count: 380 },
    ],
    history: [
      { date: '2026-06-15', score: 20710, change: 80 },
      { date: '2026-06-16', score: 20730, change: 20 },
      { date: '2026-06-17', score: 20780, change: 50 },
      { date: '2026-06-18', score: 20790, change: 10 },
      { date: '2026-06-19', score: 20820, change: 30 },
      { date: '2026-06-20', score: 20850, change: 30 },
      { date: '2026-06-21', score: 20800, change: -50 },
    ],
  },
  {
    entityId: 4,
    entityName: 'Noticias 24/7 Radio',
    entityType: 'radio',
    currentScore: 19200,
    weeklyScore: 1700,
    monthlyScore: 7200,
    allTimeScore: 19200,
    scoreChange: -200,
    rankingPosition: 8,
    rankingPreviousPosition: 6,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'declining',
    breakdown: [
      { category: 'publicaciones', points: 1250, count: 250 },
      { category: 'eventos', points: 900, count: 60 },
      { category: 'concursos', points: 1050, count: 42 },
      { category: 'comentarios', points: 1900, count: 1900 },
      { category: 'likes', points: 2800, count: 2800 },
      { category: 'compartidos', points: 1900, count: 380 },
      { category: 'seguidores', points: 3200, count: 320 },
      { category: 'clubes', points: 450, count: 30 },
      { category: 'locutores', points: 180, count: 18 },
      { category: 'programas', points: 140, count: 14 },
      { category: 'saludos', points: 240, count: 120 },
      { category: 'canciones', points: 200, count: 100 },
      { category: 'encuestas', points: 160, count: 80 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 350, count: 350 },
    ],
    history: [
      { date: '2026-06-15', score: 19400, change: -50 },
      { date: '2026-06-16', score: 19350, change: -50 },
      { date: '2026-06-17', score: 19300, change: -50 },
      { date: '2026-06-18', score: 19280, change: -20 },
      { date: '2026-06-19', score: 19300, change: 20 },
      { date: '2026-06-20', score: 19250, change: -50 },
      { date: '2026-06-21', score: 19200, change: -50 },
    ],
  },
  {
    entityId: 11,
    entityName: 'Radio Vida Nueva',
    entityType: 'radio',
    currentScore: 18500,
    weeklyScore: 1580,
    monthlyScore: 6500,
    allTimeScore: 18500,
    scoreChange: 120,
    rankingPosition: 9,
    rankingPreviousPosition: 11,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'publicaciones', points: 1100, count: 220 },
      { category: 'eventos', points: 750, count: 50 },
      { category: 'concursos', points: 900, count: 36 },
      { category: 'comentarios', points: 1700, count: 1700 },
      { category: 'likes', points: 2600, count: 2600 },
      { category: 'compartidos', points: 1700, count: 340 },
      { category: 'seguidores', points: 2200, count: 220 },
      { category: 'clubes', points: 300, count: 20 },
      { category: 'locutores', points: 160, count: 16 },
      { category: 'programas', points: 120, count: 12 },
      { category: 'saludos', points: 220, count: 110 },
      { category: 'canciones', points: 180, count: 90 },
      { category: 'encuestas', points: 140, count: 70 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 320, count: 320 },
    ],
    history: [
      { date: '2026-06-15', score: 18380, change: 100 },
      { date: '2026-06-16', score: 18400, change: 20 },
      { date: '2026-06-17', score: 18420, change: 20 },
      { date: '2026-06-18', score: 18460, change: 40 },
      { date: '2026-06-19', score: 18480, change: 20 },
      { date: '2026-06-20', score: 18520, change: 40 },
      { date: '2026-06-21', score: 18500, change: -20 },
    ],
  },
  {
    entityId: 6,
    entityName: 'Radio Comunitaria Unida',
    entityType: 'radio',
    currentScore: 16800,
    weeklyScore: 1350,
    monthlyScore: 5800,
    allTimeScore: 16800,
    scoreChange: -40,
    rankingPosition: 10,
    rankingPreviousPosition: 9,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'stable',
    breakdown: [
      { category: 'publicaciones', points: 1000, count: 200 },
      { category: 'eventos', points: 600, count: 40 },
      { category: 'concursos', points: 750, count: 30 },
      { category: 'comentarios', points: 1500, count: 1500 },
      { category: 'likes', points: 2200, count: 2200 },
      { category: 'compartidos', points: 1400, count: 280 },
      { category: 'seguidores', points: 1200, count: 120 },
      { category: 'clubes', points: 150, count: 10 },
      { category: 'locutores', points: 140, count: 14 },
      { category: 'programas', points: 100, count: 10 },
      { category: 'saludos', points: 200, count: 100 },
      { category: 'canciones', points: 160, count: 80 },
      { category: 'encuestas', points: 120, count: 60 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 280, count: 280 },
    ],
    history: [
      { date: '2026-06-15', score: 16840, change: -30 },
      { date: '2026-06-16', score: 16820, change: -20 },
      { date: '2026-06-17', score: 16860, change: 40 },
      { date: '2026-06-18', score: 16830, change: -30 },
      { date: '2026-06-19', score: 16850, change: 20 },
      { date: '2026-06-20', score: 16840, change: -10 },
      { date: '2026-06-21', score: 16800, change: -40 },
    ],
  },
  {
    entityId: 10,
    entityName: 'Radio Voz del Pueblo',
    entityType: 'radio',
    currentScore: 14500,
    weeklyScore: 980,
    monthlyScore: 4200,
    allTimeScore: 14500,
    scoreChange: 70,
    rankingPosition: 11,
    rankingPreviousPosition: 12,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'publicaciones', points: 800, count: 160 },
      { category: 'eventos', points: 450, count: 30 },
      { category: 'concursos', points: 500, count: 20 },
      { category: 'comentarios', points: 1200, count: 1200 },
      { category: 'likes', points: 1800, count: 1800 },
      { category: 'compartidos', points: 1100, count: 220 },
      { category: 'seguidores', points: 800, count: 80 },
      { category: 'clubes', points: 0, count: 0 },
      { category: 'locutores', points: 120, count: 12 },
      { category: 'programas', points: 80, count: 8 },
      { category: 'saludos', points: 180, count: 90 },
      { category: 'canciones', points: 140, count: 70 },
      { category: 'encuestas', points: 100, count: 50 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 250, count: 250 },
    ],
    history: [
      { date: '2026-06-15', score: 14430, change: 60 },
      { date: '2026-06-16', score: 14450, change: 20 },
      { date: '2026-06-17', score: 14480, change: 30 },
      { date: '2026-06-18', score: 14510, change: 30 },
      { date: '2026-06-19', score: 14490, change: -20 },
      { date: '2026-06-20', score: 14530, change: 40 },
      { date: '2026-06-21', score: 14500, change: -30 },
    ],
  },
  {
    entityId: 8,
    entityName: 'Radio Internacional Voz',
    entityType: 'radio',
    currentScore: 12800,
    weeklyScore: 740,
    monthlyScore: 3400,
    allTimeScore: 12800,
    scoreChange: -150,
    rankingPosition: 12,
    rankingPreviousPosition: 10,
    lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'declining',
    breakdown: [
      { category: 'publicaciones', points: 650, count: 130 },
      { category: 'eventos', points: 300, count: 20 },
      { category: 'concursos', points: 350, count: 14 },
      { category: 'comentarios', points: 1000, count: 1000 },
      { category: 'likes', points: 1500, count: 1500 },
      { category: 'compartidos', points: 900, count: 180 },
      { category: 'seguidores', points: 1800, count: 180 },
      { category: 'clubes', points: 0, count: 0 },
      { category: 'locutores', points: 100, count: 10 },
      { category: 'programas', points: 60, count: 6 },
      { category: 'saludos', points: 160, count: 80 },
      { category: 'canciones', points: 120, count: 60 },
      { category: 'encuestas', points: 80, count: 40 },
      { category: 'verificacion', points: 100, count: 1 },
      { category: 'horas_radio', points: 220, count: 220 },
    ],
    history: [
      { date: '2026-06-15', score: 12950, change: -30 },
      { date: '2026-06-16', score: 12900, change: -50 },
      { date: '2026-06-17', score: 12880, change: -20 },
      { date: '2026-06-18', score: 12850, change: -30 },
      { date: '2026-06-19', score: 12900, change: 50 },
      { date: '2026-06-20', score: 12840, change: -60 },
      { date: '2026-06-21', score: 12800, change: -40 },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   LOCUTOR SCORES — 12 locutores
   ═══════════════════════════════════════════════════════════════════ */
export const hostScores: RfwScoreData[] = [
  {
    entityId: 11, entityName: 'Ricky Navarro', entityType: 'locutor',
    currentScore: 18200, weeklyScore: 2100, monthlyScore: 7400, allTimeScore: 18200,
    scoreChange: 380, rankingPosition: 1, rankingPreviousPosition: 2, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'transmisiones', points: 2100, count: 210 },
      { category: 'seguidores', points: 3500, count: 700 },
      { category: 'likes', points: 4200, count: 4200 },
      { category: 'compartidos', points: 2800, count: 560 },
      { category: 'saludos', points: 1800, count: 900 },
      { category: 'canciones', points: 1600, count: 800 },
      { category: 'comentarios', points: 2100, count: 2100 },
      { category: 'eventos', points: 500, count: 50 },
    ],
    history: [
      { date: '2026-06-15', score: 17820, change: 320 }, { date: '2026-06-16', score: 17900, change: 80 },
      { date: '2026-06-17', score: 18020, change: 120 }, { date: '2026-06-18', score: 18100, change: 80 },
      { date: '2026-06-19', score: 18180, change: 80 }, { date: '2026-06-20', score: 18250, change: 70 },
      { date: '2026-06-21', score: 18200, change: -50 },
    ],
  },
  {
    entityId: 7, entityName: 'Diego Quintero', entityType: 'locutor',
    currentScore: 16800, weeklyScore: 1950, monthlyScore: 6800, allTimeScore: 16800,
    scoreChange: 250, rankingPosition: 2, rankingPreviousPosition: 3, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'transmisiones', points: 1800, count: 180 },
      { category: 'seguidores', points: 3000, count: 600 },
      { category: 'likes', points: 3800, count: 3800 },
      { category: 'compartidos', points: 2500, count: 500 },
      { category: 'saludos', points: 1500, count: 750 },
      { category: 'canciones', points: 1400, count: 700 },
      { category: 'comentarios', points: 1900, count: 1900 },
      { category: 'eventos', points: 400, count: 40 },
    ],
    history: [
      { date: '2026-06-15', score: 16550, change: 200 }, { date: '2026-06-16', score: 16600, change: 50 },
      { date: '2026-06-17', score: 16700, change: 100 }, { date: '2026-06-18', score: 16750, change: 50 },
      { date: '2026-06-19', score: 16800, change: 50 }, { date: '2026-06-20', score: 16850, change: 50 },
      { date: '2026-06-21', score: 16800, change: -50 },
    ],
  },
  {
    entityId: 2, entityName: 'Valentina Ríos', entityType: 'locutor',
    currentScore: 15400, weeklyScore: 1580, monthlyScore: 6200, allTimeScore: 15400,
    scoreChange: 120, rankingPosition: 3, rankingPreviousPosition: 4, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'trending',
    breakdown: [
      { category: 'transmisiones', points: 1600, count: 160 },
      { category: 'seguidores', points: 2800, count: 560 },
      { category: 'likes', points: 3500, count: 3500 },
      { category: 'compartidos', points: 2200, count: 440 },
      { category: 'saludos', points: 1400, count: 700 },
      { category: 'canciones', points: 1200, count: 600 },
      { category: 'comentarios', points: 1700, count: 1700 },
      { category: 'eventos', points: 300, count: 30 },
    ],
    history: [
      { date: '2026-06-15', score: 15280, change: 100 }, { date: '2026-06-16', score: 15300, change: 20 },
      { date: '2026-06-17', score: 15350, change: 50 }, { date: '2026-06-18', score: 15380, change: 30 },
      { date: '2026-06-19', score: 15420, change: 40 }, { date: '2026-06-20', score: 15450, change: 30 },
      { date: '2026-06-21', score: 15400, change: -50 },
    ],
  },
  {
    entityId: 3, entityName: 'Beto Fuentes', entityType: 'locutor',
    currentScore: 14600, weeklyScore: 1820, monthlyScore: 5900, allTimeScore: 14600,
    scoreChange: -200, rankingPosition: 4, rankingPreviousPosition: 1, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'declining',
    breakdown: [
      { category: 'transmisiones', points: 1500, count: 150 },
      { category: 'seguidores', points: 2600, count: 520 },
      { category: 'likes', points: 3200, count: 3200 },
      { category: 'compartidos', points: 2000, count: 400 },
      { category: 'saludos', points: 1300, count: 650 },
      { category: 'canciones', points: 1100, count: 550 },
      { category: 'comentarios', points: 1600, count: 1600 },
      { category: 'eventos', points: 300, count: 30 },
    ],
    history: [
      { date: '2026-06-15', score: 14800, change: -80 }, { date: '2026-06-16', score: 14750, change: -50 },
      { date: '2026-06-17', score: 14700, change: -50 }, { date: '2026-06-18', score: 14680, change: -20 },
      { date: '2026-06-19', score: 14650, change: -30 }, { date: '2026-06-20', score: 14700, change: 50 },
      { date: '2026-06-21', score: 14600, change: -100 },
    ],
  },
  {
    entityId: 1, entityName: 'Martín Castellanos', entityType: 'locutor',
    currentScore: 13800, weeklyScore: 1420, monthlyScore: 5500, allTimeScore: 13800,
    scoreChange: 80, rankingPosition: 5, rankingPreviousPosition: 5, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'stable',
    breakdown: [
      { category: 'transmisiones', points: 1400, count: 140 },
      { category: 'seguidores', points: 2400, count: 480 },
      { category: 'likes', points: 2900, count: 2900 },
      { category: 'compartidos', points: 1800, count: 360 },
      { category: 'saludos', points: 1200, count: 600 },
      { category: 'canciones', points: 1000, count: 500 },
      { category: 'comentarios', points: 1400, count: 1400 },
      { category: 'eventos', points: 200, count: 20 },
    ],
    history: [
      { date: '2026-06-15', score: 13720, change: 60 }, { date: '2026-06-16', score: 13750, change: 30 },
      { date: '2026-06-17', score: 13740, change: -10 }, { date: '2026-06-18', score: 13780, change: 40 },
      { date: '2026-06-19', score: 13790, change: 10 }, { date: '2026-06-20', score: 13820, change: 30 },
      { date: '2026-06-21', score: 13800, change: -20 },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   PROGRAMA SCORES — 12 programas
   ═══════════════════════════════════════════════════════════════════ */
export const programScores: RfwScoreData[] = [
  {
    entityId: 2, entityName: 'Tardes con Vale', entityType: 'programa',
    currentScore: 21500, weeklyScore: 2800, monthlyScore: 8900, allTimeScore: 21500,
    scoreChange: 420, rankingPosition: 1, rankingPreviousPosition: 2, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'seguidores', points: 5000, count: 1000 },
      { category: 'comentarios', points: 3800, count: 3800 },
      { category: 'likes', points: 4800, count: 4800 },
      { category: 'compartidos', points: 2200, count: 440 },
      { category: 'participacion_concursos', points: 1200, count: 240 },
      { category: 'menciones', points: 1800, count: 900 },
      { category: 'escuchas_activas', points: 2200, count: 2200 },
    ],
    history: [
      { date: '2026-06-15', score: 21080, change: 380 }, { date: '2026-06-16', score: 21150, change: 70 },
      { date: '2026-06-17', score: 21250, change: 100 }, { date: '2026-06-18', score: 21380, change: 130 },
      { date: '2026-06-19', score: 21450, change: 70 }, { date: '2026-06-20', score: 21550, change: 100 },
      { date: '2026-06-21', score: 21500, change: -50 },
    ],
  },
  {
    entityId: 11, entityName: 'El Desparche', entityType: 'programa',
    currentScore: 20800, weeklyScore: 2600, monthlyScore: 8400, allTimeScore: 20800,
    scoreChange: 350, rankingPosition: 2, rankingPreviousPosition: 3, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'seguidores', points: 3800, count: 760 },
      { category: 'comentarios', points: 4800, count: 4800 },
      { category: 'likes', points: 5200, count: 5200 },
      { category: 'compartidos', points: 2800, count: 560 },
      { category: 'participacion_concursos', points: 800, count: 160 },
      { category: 'menciones', points: 1600, count: 800 },
      { category: 'escuchas_activas', points: 2000, count: 2000 },
    ],
    history: [
      { date: '2026-06-15', score: 20450, change: 300 }, { date: '2026-06-16', score: 20520, change: 70 },
      { date: '2026-06-17', score: 20600, change: 80 }, { date: '2026-06-18', score: 20700, change: 100 },
      { date: '2026-06-19', score: 20780, change: 80 }, { date: '2026-06-20', score: 20850, change: 70 },
      { date: '2026-06-21', score: 20800, change: -50 },
    ],
  },
  {
    entityId: 7, entityName: 'Modo Creativo', entityType: 'programa',
    currentScore: 19600, weeklyScore: 2400, monthlyScore: 7800, allTimeScore: 19600,
    scoreChange: 480, rankingPosition: 3, rankingPreviousPosition: 5, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'seguidores', points: 4200, count: 840 },
      { category: 'comentarios', points: 4200, count: 4200 },
      { category: 'likes', points: 4500, count: 4500 },
      { category: 'compartidos', points: 2600, count: 520 },
      { category: 'participacion_concursos', points: 700, count: 140 },
      { category: 'menciones', points: 1500, count: 750 },
      { category: 'escuchas_activas', points: 1900, count: 1900 },
    ],
    history: [
      { date: '2026-06-15', score: 19120, change: 400 }, { date: '2026-06-16', score: 19200, change: 80 },
      { date: '2026-06-17', score: 19300, change: 100 }, { date: '2026-06-18', score: 19450, change: 150 },
      { date: '2026-06-19', score: 19550, change: 100 }, { date: '2026-06-20', score: 19650, change: 100 },
      { date: '2026-06-21', score: 19600, change: -50 },
    ],
  },
  {
    entityId: 3, entityName: 'Deporte Total', entityType: 'programa',
    currentScore: 18400, weeklyScore: 2100, monthlyScore: 7200, allTimeScore: 18400,
    scoreChange: -100, rankingPosition: 4, rankingPreviousPosition: 1, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'active',
    breakdown: [
      { category: 'seguidores', points: 4000, count: 800 },
      { category: 'comentarios', points: 3600, count: 3600 },
      { category: 'likes', points: 4000, count: 4000 },
      { category: 'compartidos', points: 2000, count: 400 },
      { category: 'participacion_concursos', points: 1000, count: 200 },
      { category: 'menciones', points: 1400, count: 700 },
      { category: 'escuchas_activas', points: 1800, count: 1800 },
    ],
    history: [
      { date: '2026-06-15', score: 18500, change: -80 }, { date: '2026-06-16', score: 18450, change: -50 },
      { date: '2026-06-17', score: 18420, change: -30 }, { date: '2026-06-18', score: 18450, change: 30 },
      { date: '2026-06-19', score: 18430, change: -20 }, { date: '2026-06-20', score: 18500, change: 70 },
      { date: '2026-06-21', score: 18400, change: -100 },
    ],
  },
  {
    entityId: 1, entityName: 'Mañanas Argentinas', entityType: 'programa',
    currentScore: 16800, weeklyScore: 1700, monthlyScore: 6500, allTimeScore: 16800,
    scoreChange: 60, rankingPosition: 5, rankingPreviousPosition: 4, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'stable',
    breakdown: [
      { category: 'seguidores', points: 3200, count: 640 },
      { category: 'comentarios', points: 2800, count: 2800 },
      { category: 'likes', points: 3200, count: 3200 },
      { category: 'compartidos', points: 1600, count: 320 },
      { category: 'participacion_concursos', points: 600, count: 120 },
      { category: 'menciones', points: 1200, count: 600 },
      { category: 'escuchas_activas', points: 1600, count: 1600 },
    ],
    history: [
      { date: '2026-06-15', score: 16740, change: 50 }, { date: '2026-06-16', score: 16760, change: 20 },
      { date: '2026-06-17', score: 16750, change: -10 }, { date: '2026-06-18', score: 16800, change: 50 },
      { date: '2026-06-19', score: 16790, change: -10 }, { date: '2026-06-20', score: 16820, change: 30 },
      { date: '2026-06-21', score: 16800, change: -20 },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   OYENTE SCORES — 10 oyentes
   ═══════════════════════════════════════════════════════════════════ */
export const listenerScores: RfwScoreData[] = [
  {
    entityId: 1, entityName: 'Javier Mendoza', entityType: 'oyente',
    currentScore: 9800, weeklyScore: 850, monthlyScore: 3800, allTimeScore: 9800,
    scoreChange: 85, rankingPosition: 1, rankingPreviousPosition: 1, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'perfil_creado', points: 20, count: 1 },
      { category: 'saludos', points: 1200, count: 120 },
      { category: 'comentarios', points: 1500, count: 150 },
      { category: 'canciones', points: 800, count: 80 },
      { category: 'seguidores', points: 1200, count: 240 },
      { category: 'compartidos', points: 1800, count: 180 },
      { category: 'participacion_concursos', points: 900, count: 60 },
      { category: 'ganador_concurso', points: 500, count: 10 },
      { category: 'asistir_evento', points: 800, count: 40 },
      { category: 'invitar_amigo', points: 1000, count: 40 },
    ],
    history: [
      { date: '2026-06-15', score: 9715, change: 70 }, { date: '2026-06-16', score: 9740, change: 25 },
      { date: '2026-06-17', score: 9770, change: 30 }, { date: '2026-06-18', score: 9790, change: 20 },
      { date: '2026-06-19', score: 9820, change: 30 }, { date: '2026-06-20', score: 9840, change: 20 },
      { date: '2026-06-21', score: 9800, change: -40 },
    ],
  },
  {
    entityId: 2, entityName: 'Isabella Torres', entityType: 'oyente',
    currentScore: 8600, weeklyScore: 720, monthlyScore: 3100, allTimeScore: 8600,
    scoreChange: 120, rankingPosition: 2, rankingPreviousPosition: 3, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'trending',
    breakdown: [
      { category: 'perfil_creado', points: 20, count: 1 },
      { category: 'saludos', points: 900, count: 90 },
      { category: 'comentarios', points: 1200, count: 120 },
      { category: 'canciones', points: 700, count: 70 },
      { category: 'seguidores', points: 1000, count: 200 },
      { category: 'compartidos', points: 1500, count: 150 },
      { category: 'participacion_concursos', points: 750, count: 50 },
      { category: 'ganador_concurso', points: 250, count: 5 },
      { category: 'asistir_evento', points: 600, count: 30 },
      { category: 'invitar_amigo', points: 750, count: 30 },
    ],
    history: [
      { date: '2026-06-15', score: 8480, change: 100 }, { date: '2026-06-16', score: 8500, change: 20 },
      { date: '2026-06-17', score: 8540, change: 40 }, { date: '2026-06-18', score: 8570, change: 30 },
      { date: '2026-06-19', score: 8600, change: 30 }, { date: '2026-06-20', score: 8620, change: 20 },
      { date: '2026-06-21', score: 8600, change: -20 },
    ],
  },
  {
    entityId: 3, entityName: 'Felipe Gutiérrez', entityType: 'oyente',
    currentScore: 7400, weeklyScore: 580, monthlyScore: 2650, allTimeScore: 7400,
    scoreChange: -30, rankingPosition: 3, rankingPreviousPosition: 2, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'stable',
    breakdown: [
      { category: 'perfil_creado', points: 20, count: 1 },
      { category: 'saludos', points: 700, count: 70 },
      { category: 'comentarios', points: 1000, count: 100 },
      { category: 'canciones', points: 600, count: 60 },
      { category: 'seguidores', points: 800, count: 160 },
      { category: 'compartidos', points: 1200, count: 120 },
      { category: 'participacion_concursos', points: 450, count: 30 },
      { category: 'asistir_evento', points: 400, count: 20 },
      { category: 'invitar_amigo', points: 500, count: 20 },
    ],
    history: [
      { date: '2026-06-15', score: 7430, change: -20 }, { date: '2026-06-16', score: 7410, change: -20 },
      { date: '2026-06-17', score: 7420, change: 10 }, { date: '2026-06-18', score: 7400, change: -20 },
      { date: '2026-06-19', score: 7425, change: 25 }, { date: '2026-06-20', score: 7410, change: -15 },
      { date: '2026-06-21', score: 7400, change: -10 },
    ],
  },
  {
    entityId: 4, entityName: 'Adriana Mejía', entityType: 'oyente',
    currentScore: 6500, weeklyScore: 620, monthlyScore: 2400, allTimeScore: 6500,
    scoreChange: 200, rankingPosition: 4, rankingPreviousPosition: 6, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'perfil_creado', points: 20, count: 1 },
      { category: 'saludos', points: 500, count: 50 },
      { category: 'comentarios', points: 800, count: 80 },
      { category: 'canciones', points: 500, count: 50 },
      { category: 'seguidores', points: 700, count: 140 },
      { category: 'compartidos', points: 1000, count: 100 },
      { category: 'participacion_concursos', points: 300, count: 20 },
      { category: 'asistir_evento', points: 300, count: 15 },
      { category: 'invitar_amigo', points: 250, count: 10 },
    ],
    history: [
      { date: '2026-06-15', score: 6300, change: 180 }, { date: '2026-06-16', score: 6340, change: 40 },
      { date: '2026-06-17', score: 6380, change: 40 }, { date: '2026-06-18', score: 6420, change: 40 },
      { date: '2026-06-19', score: 6480, change: 60 }, { date: '2026-06-20', score: 6520, change: 40 },
      { date: '2026-06-21', score: 6500, change: -20 },
    ],
  },
  {
    entityId: 5, entityName: 'Camila Restrepo', entityType: 'oyente',
    currentScore: 5800, weeklyScore: 390, monthlyScore: 1800, allTimeScore: 5800,
    scoreChange: -80, rankingPosition: 5, rankingPreviousPosition: 4, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'declining',
    breakdown: [
      { category: 'perfil_creado', points: 20, count: 1 },
      { category: 'saludos', points: 400, count: 40 },
      { category: 'comentarios', points: 600, count: 60 },
      { category: 'canciones', points: 400, count: 40 },
      { category: 'seguidores', points: 500, count: 100 },
      { category: 'compartidos', points: 800, count: 80 },
      { category: 'participacion_concursos', points: 150, count: 10 },
      { category: 'asistir_evento', points: 200, count: 10 },
    ],
    history: [
      { date: '2026-06-15', score: 5880, change: -40 }, { date: '2026-06-16', score: 5850, change: -30 },
      { date: '2026-06-17', score: 5840, change: -10 }, { date: '2026-06-18', score: 5820, change: -20 },
      { date: '2026-06-19', score: 5850, change: 30 }, { date: '2026-06-20', score: 5830, change: -20 },
      { date: '2026-06-21', score: 5800, change: -30 },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   CLUB SCORES — 6 clubes
   ═══════════════════════════════════════════════════════════════════ */
export const clubScores: RfwScoreData[] = [
  {
    entityId: 1, entityName: 'Club Éxito VIP', entityType: 'club',
    currentScore: 12500, weeklyScore: 1100, monthlyScore: 4600, allTimeScore: 12500,
    scoreChange: 200, rankingPosition: 1, rankingPreviousPosition: 1, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'clubes', points: 3200, count: 320 },
      { category: 'actividad_miembro', points: 4800, count: 4800 },
      { category: 'compartidos', points: 1800, count: 360 },
      { category: 'eventos', points: 800, count: 80 },
      { category: 'concursos', points: 600, count: 40 },
      { category: 'victoria_competencia', points: 300, count: 6 },
    ],
    history: [
      { date: '2026-06-15', score: 12300, change: 160 }, { date: '2026-06-16', score: 12350, change: 50 },
      { date: '2026-06-17', score: 12400, change: 50 }, { date: '2026-06-18', score: 12460, change: 60 },
      { date: '2026-06-19', score: 12500, change: 40 }, { date: '2026-06-20', score: 12550, change: 50 },
      { date: '2026-06-21', score: 12500, change: -50 },
    ],
  },
  {
    entityId: 2, entityName: 'Club Capital Fans', entityType: 'club',
    currentScore: 10800, weeklyScore: 900, monthlyScore: 3900, allTimeScore: 10800,
    scoreChange: 150, rankingPosition: 2, rankingPreviousPosition: 3, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'clubes', points: 2500, count: 250 },
      { category: 'actividad_miembro', points: 4200, count: 4200 },
      { category: 'compartidos', points: 1500, count: 300 },
      { category: 'eventos', points: 600, count: 60 },
      { category: 'concursos', points: 450, count: 30 },
    ],
    history: [
      { date: '2026-06-15', score: 10650, change: 120 }, { date: '2026-06-16', score: 10700, change: 50 },
      { date: '2026-06-17', score: 10750, change: 50 }, { date: '2026-06-18', score: 10800, change: 50 },
      { date: '2026-06-19', score: 10830, change: 30 }, { date: '2026-06-20', score: 10860, change: 30 },
      { date: '2026-06-21', score: 10800, change: -60 },
    ],
  },
  {
    entityId: 3, entityName: 'Club Deportivo Total', entityType: 'club',
    currentScore: 9500, weeklyScore: 780, monthlyScore: 3400, allTimeScore: 9500,
    scoreChange: -50, rankingPosition: 3, rankingPreviousPosition: 2, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'stable',
    breakdown: [
      { category: 'clubes', points: 2200, count: 220 },
      { category: 'actividad_miembro', points: 3800, count: 3800 },
      { category: 'compartidos', points: 1300, count: 260 },
      { category: 'eventos', points: 500, count: 50 },
      { category: 'concursos', points: 300, count: 20 },
    ],
    history: [
      { date: '2026-06-15', score: 9550, change: -30 }, { date: '2026-06-16', score: 9520, change: -30 },
      { date: '2026-06-17', score: 9540, change: 20 }, { date: '2026-06-18', score: 9530, change: -10 },
      { date: '2026-06-19', score: 9560, change: 30 }, { date: '2026-06-20', score: 9540, change: -20 },
      { date: '2026-06-21', score: 9500, change: -40 },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   HELPER: Get score by entity type and id
   ═══════════════════════════════════════════════════════════════════ */
export function getRfwScore(entityType: 'radio' | 'programa' | 'locutor' | 'club' | 'oyente', entityId: number): RfwScoreData | undefined {
  switch (entityType) {
    case 'radio': return radioScores.find(s => s.entityId === entityId);
    case 'programa': return programScores.find(s => s.entityId === entityId);
    case 'locutor': return hostScores.find(s => s.entityId === entityId);
    case 'club': return clubScores.find(s => s.entityId === entityId);
    case 'oyente': return listenerScores.find(s => s.entityId === entityId);
    default: return undefined;
  }
}

export function getScoreByRankingPosition(entityType: 'radio' | 'programa' | 'locutor' | 'club' | 'oyente', position: number): RfwScoreData | undefined {
  const collection = (() => {
    switch (entityType) { case 'radio': return radioScores; case 'programa': return programScores; case 'locutor': return hostScores; case 'club': return clubScores; case 'oyente': return listenerScores; default: return []; }
  })();
  return collection.find(s => s.rankingPosition === position);
}

export function getTopScores(entityType: 'radio' | 'programa' | 'locutor' | 'club' | 'oyente', limit: number = 5): RfwScoreData[] {
  const collection = (() => {
    switch (entityType) { case 'radio': return radioScores; case 'programa': return programScores; case 'locutor': return hostScores; case 'club': return clubScores; case 'oyente': return listenerScores; default: return []; }
  })();
  return [...collection].sort((a, b) => b.currentScore - a.currentScore).slice(0, limit);
}

export function getRisingEntities(): RfwRankingEntry[] {
  const all: RfwScoreData[] = [...radioScores, ...programScores, ...hostScores, ...clubScores, ...listenerScores];
  const withGrowth = all.map(s => {
    const prevWeekScore = s.history.length >= 7 ? s.history[s.history.length - 7].score : s.currentScore - s.weeklyScore;
    const growthPercent = prevWeekScore > 0 ? ((s.currentScore - prevWeekScore) / prevWeekScore) * 100 : 0;
    return { ...s, growthPercent };
  });
  const sorted = withGrowth.sort((a, b) => b.growthPercent - a.growthPercent).slice(0, 15);
  return sorted.map((s, i) => ({
    position: i + 1,
    previousPosition: s.rankingPreviousPosition,
    entityType: s.entityType,
    entityId: s.entityId,
    entityName: s.entityName,
    entitySubtitle: `${s.entityType.charAt(0).toUpperCase() + s.entityType.slice(1)} · ${s.currentScore.toLocaleString()} pts`,
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Abstract%20modern%20radio%20frequency%20waves%20gradient%20background%2C%20minimalist%20digital%20art%20with%20flowing%20curves%2C%20warm%20amber%20and%20gold%20tones%2C%20audio%20visualization%20style%2C%20elegant%20premium%20aesthetic%2C%20no%20text%2C%20smooth%20flowing%20lines%20representing%20growth%20and%20energy&width=200&height=200&seq=rfw-rising-generic&orientation=squarish',
    currentScore: s.currentScore,
    scoreChange: s.scoreChange,
    trend: s.trend,
    growthPercent: Math.round(s.growthPercent * 10) / 10,
  }));
}

/* ═══════════════════════════════════════════════════════════════════
   TIME FILTERS
   ═══════════════════════════════════════════════════════════════════ */
export type TimeFilter = 'today' | 'week' | 'month' | 'year' | 'all_time';

export const timeFilterLabels: Record<TimeFilter, string> = {
  today: 'Hoy',
  week: 'Semana',
  month: 'Mes',
  year: 'Año',
  all_time: 'Histórico',
};

export const timeFilterIcons: Record<TimeFilter, string> = {
  today: 'ri-calendar-line',
  week: 'ri-calendar-2-line',
  month: 'ri-calendar-check-line',
  year: 'ri-calendar-todo-line',
  all_time: 'ri-history-line',
};

/* ═══════════════════════════════════════════════════════════════════
   ENTITY TYPE (for ranking tabs)
   ═══════════════════════════════════════════════════════════════════ */
export type RankingEntityType = 'radios' | 'programas' | 'locutores' | 'clubes' | 'fans' | 'eventos' | 'concursos';

export const entityTypeLabels: Record<RankingEntityType, string> = {
  radios: 'Radios',
  programas: 'Programas',
  locutores: 'Locutores',
  clubes: 'Clubes',
  fans: 'Fans',
  eventos: 'Eventos',
  concursos: 'Concursos',
};

export const entityTypeIcons: Record<RankingEntityType, string> = {
  radios: 'ri-radio-line',
  programas: 'ri-calendar-check-line',
  locutores: 'ri-mic-line',
  clubes: 'ri-vip-crown-line',
  fans: 'ri-heart-pulse-line',
  eventos: 'ri-calendar-event-line',
  concursos: 'ri-trophy-line',
};

export const entityTypeDescriptions: Record<RankingEntityType, string> = {
  radios: 'Ranking basado en el Score RFW. Mide COMUNIDAD, no solo audiencia.',
  programas: 'Programas con mayor interacción y comunidad activa.',
  locutores: 'Locutores que generan más engagement con su audiencia.',
  clubes: 'Clubes VIP con miembros más activos y participativos.',
  fans: 'Oyentes que más participan e interactúan en la plataforma.',
  eventos: 'Eventos con mayor asistencia, participación y rating.',
  concursos: 'Concursos con más participantes y engagement.',
};

/* ═══════════════════════════════════════════════════════════════════
   DISPLAY RANKING ENTRY — Rich data for UI
   ═══════════════════════════════════════════════════════════════════ */
export interface DisplayRankingEntry {
  position: number;
  previousPosition: number;
  entityId: number;
  entityName: string;
  entitySubtitle: string;
  logoUrl: string;
  score: number;
  scoreLabel: string;
  scoreChange: number;
  trend: ScoreTrend;
  followers?: number;
  isLive?: boolean;
  highlight?: string;
  trophy?: 'gold' | 'silver' | 'bronze' | 'top10' | 'top100' | null;
}

/* ═══════════════════════════════════════════════════════════════════
   RANKING GENERATORS
   ═══════════════════════════════════════════════════════════════════ */

import { globalRadioStations } from '@/mocks/globalRadios';
import { broadcasters } from '@/mocks/broadcasters';
import { programs } from '@/mocks/programs';

function getScoreForTime(s: RfwScoreData, tf: TimeFilter): number {
  switch (tf) {
    case 'today': return s.history.length > 0 ? s.history[s.history.length - 1].score : s.currentScore;
    case 'week': return s.weeklyScore;
    case 'month': return s.monthlyScore;
    case 'year': return Math.round(s.allTimeScore * 0.8);
    case 'all_time': return s.allTimeScore;
    default: return s.currentScore;
  }
}

function getScoreLabelForTime(s: RfwScoreData, tf: TimeFilter): string {
  const sc = getScoreForTime(s, tf);
  if (sc >= 1000) return `${(sc / 1000).toFixed(1)}K pts`;
  return `${sc.toLocaleString()} pts`;
}

export function getRadioRanking(timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  const stationMap = new Map(globalRadioStations.map(st => [st.id, st]));

  return radioScores
    .map((s) => {
      const station = stationMap.get(s.entityId);
      const score = getScoreForTime(s, timeFilter);
      return {
        s,
        station,
        score,
        scoreLabel: getScoreLabelForTime(s, timeFilter),
        positionDelta: s.rankingPreviousPosition - s.rankingPosition,
      };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry, i) => {
      const pos = i + 1;
      const station = entry.station;
      let trophy: DisplayRankingEntry['trophy'] = null;
      if (pos === 1) trophy = 'gold';
      else if (pos === 2) trophy = 'silver';
      else if (pos === 3) trophy = 'bronze';
      else if (pos <= 10) trophy = 'top10';
      else if (pos <= 100) trophy = 'top100';

      return {
        position: pos,
        previousPosition: entry.s.rankingPreviousPosition,
        entityId: entry.s.entityId,
        entityName: entry.s.entityName,
        entitySubtitle: station ? `${station.frequency} · ${station.city}, ${station.state}` : `Radio · ${entry.s.entityName}`,
        logoUrl: station?.logoUrl || '',
        score: entry.score,
        scoreLabel: entry.scoreLabel,
        scoreChange: entry.s.scoreChange,
        trend: entry.s.trend,
        followers: station?.followers ?? 0,
        isLive: station?.isLive ?? false,
        highlight: station?.category ?? '',
        trophy,
      };
    });
}

export function getHostRanking(timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  const hostMap = new Map(broadcasters?.map((h: any) => [h.id, h]) || []);

  return hostScores
    .map((s) => {
      const host = hostMap.get(s.entityId) as any;
      const score = getScoreForTime(s, timeFilter);
      return { s, host, score, scoreLabel: getScoreLabelForTime(s, timeFilter) };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry, i) => {
      const pos = i + 1;
      let trophy: DisplayRankingEntry['trophy'] = null;
      if (pos === 1) trophy = 'gold';
      else if (pos === 2) trophy = 'silver';
      else if (pos === 3) trophy = 'bronze';
      else if (pos <= 10) trophy = 'top10';
      else if (pos <= 100) trophy = 'top100';

      return {
        position: pos,
        previousPosition: entry.s.rankingPreviousPosition,
        entityId: entry.s.entityId,
        entityName: entry.s.entityName,
        entitySubtitle: entry.host?.city ? `Locutor · ${entry.host.city}` : 'Locutor',
        logoUrl: entry.host?.photoUrl || '',
        score: entry.score,
        scoreLabel: entry.scoreLabel,
        scoreChange: entry.s.scoreChange,
        trend: entry.s.trend,
        followers: entry.host?.metrics?.followers ?? 0,
        isLive: entry.host?.status === 'activo',
        highlight: entry.host?.specialties?.[0] ?? '',
        trophy,
      };
    });
}

export function getProgramRanking(timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  const programMap = new Map((programs as any[])?.map((p: any) => [p.id, p]) || []);

  return programScores
    .map((s) => {
      const prog = programMap.get(s.entityId) as any;
      const score = getScoreForTime(s, timeFilter);
      return { s, prog, score, scoreLabel: getScoreLabelForTime(s, timeFilter) };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry, i) => {
      const pos = i + 1;
      let trophy: DisplayRankingEntry['trophy'] = null;
      if (pos === 1) trophy = 'gold';
      else if (pos === 2) trophy = 'silver';
      else if (pos === 3) trophy = 'bronze';
      else if (pos <= 10) trophy = 'top10';
      else if (pos <= 100) trophy = 'top100';

      return {
        position: pos,
        previousPosition: entry.s.rankingPreviousPosition,
        entityId: entry.s.entityId,
        entityName: entry.s.entityName,
        entitySubtitle: entry.prog?.radioName ? `Programa · ${entry.prog.radioName}` : 'Programa',
        logoUrl: entry.prog?.coverUrl || '',
        score: entry.score,
        scoreLabel: entry.scoreLabel,
        scoreChange: entry.s.scoreChange,
        trend: entry.s.trend,
        followers: entry.prog?.followers ?? 0,
        isLive: entry.prog?.isActive ?? false,
        highlight: entry.prog?.category ?? '',
        trophy,
      };
    });
}

export function getClubRanking(timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  return clubScores
    .map((s) => {
      const score = getScoreForTime(s, timeFilter);
      return { s, score, scoreLabel: getScoreLabelForTime(s, timeFilter) };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry, i) => {
      const pos = i + 1;
      let trophy: DisplayRankingEntry['trophy'] = null;
      if (pos === 1) trophy = 'gold';
      else if (pos === 2) trophy = 'silver';
      else if (pos === 3) trophy = 'bronze';
      else if (pos <= 10) trophy = 'top10';
      else if (pos <= 100) trophy = 'top100';

      return {
        position: pos,
        previousPosition: entry.s.rankingPreviousPosition,
        entityId: entry.s.entityId,
        entityName: entry.s.entityName,
        entitySubtitle: 'Club VIP',
        logoUrl: 'https://readdy.ai/api/search-image?query=Elite%20VIP%20club%20emblem%20badge%20with%20crown%20and%20diamond%20in%20circular%20design%2C%20luxurious%20gold%20and%20black%20color%20scheme%2C%20premium%20exclusive%20feel%2C%20clean%20minimalist%20emblem%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design%20with%20subtle%20gradient&width=200&height=200&seq=club-badge-generic&orientation=squarish',
        score: entry.score,
        scoreLabel: entry.scoreLabel,
        scoreChange: entry.s.scoreChange,
        trend: entry.s.trend,
        followers: entry.s.rankingPosition === 1 ? 320 : entry.s.rankingPosition === 2 ? 250 : 220,
        highlight: 'Club',
        trophy,
      };
    });
}

/* ─── Fan avatar URLs ─── */
const fanAvatars: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20friendly%20latino%20man%20in%20his%2030s%2C%20warm%20smile%2C%20casual%20modern%20clothing%2C%20clean%20studio%20background%20with%20soft%20lighting%2C%20approachable%20and%20genuine%20expression%2C%20high%20quality%20headshot%20photography%2C%20neutral%20warm%20tone%20background&width=200&height=200&seq=fan-avatar-1&orientation=squarish',
  2: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20smiling%20latina%20woman%20in%20her%20late%2020s%2C%20confident%20and%20warm%20expression%2C%20modern%20casual%20style%2C%20clean%20studio%20background%20with%20soft%20natural%20light%2C%20high%20quality%20headshot%20photography%2C%20neutral%20cream%20background&width=200&height=200&seq=fan-avatar-2&orientation=squarish',
  3: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20cheerful%20latino%20man%20in%20his%20mid%2020s%2C%20relaxed%20smile%2C%20casual%20streetwear%20style%2C%20clean%20studio%20background%20with%20warm%20ambient%20lighting%2C%20high%20quality%20headshot%20photography%2C%20neutral%20beige%20background&width=200&height=200&seq=fan-avatar-3&orientation=squarish',
  4: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20radiant%20young%20latina%20woman%20in%20her%20early%2030s%2C%20warm%20genuine%20smile%2C%20modern%20professional%20casual%20look%2C%20clean%20studio%20background%20with%20soft%20diffused%20light%2C%20high%20quality%20headshot%20photography%2C%20neutral%20warm%20gray%20background&width=200&height=200&seq=fan-avatar-4&orientation=squarish',
  5: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20thoughtful%20latino%20man%20in%20his%20mid%2030s%2C%20subtle%20confident%20expression%2C%20smart%20casual%20attire%2C%20clean%20studio%20background%20with%20directional%20lighting%2C%20high%20quality%20headshot%20photography%2C%20neutral%20charcoal%20background&width=200&height=200&seq=fan-avatar-5&orientation=squarish',
};

export function getFanRanking(timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  return listenerScores
    .map((s) => {
      const score = getScoreForTime(s, timeFilter);
      return { s, score, scoreLabel: getScoreLabelForTime(s, timeFilter) };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry, i) => {
      const pos = i + 1;
      let trophy: DisplayRankingEntry['trophy'] = null;
      if (pos === 1) trophy = 'gold';
      else if (pos === 2) trophy = 'silver';
      else if (pos === 3) trophy = 'bronze';
      else if (pos <= 10) trophy = 'top10';
      else if (pos <= 100) trophy = 'top100';

      const totalActions = entry.s.breakdown.reduce((sum, b) => sum + b.count, 0);

      return {
        position: pos,
        previousPosition: entry.s.rankingPreviousPosition,
        entityId: entry.s.entityId,
        entityName: entry.s.entityName,
        entitySubtitle: `${totalActions} acciones totales`,
        logoUrl: fanAvatars[entry.s.entityId] || '',
        score: entry.score,
        scoreLabel: entry.scoreLabel,
        scoreChange: entry.s.scoreChange,
        trend: entry.s.trend,
        highlight: entry.s.trend === 'exploding' ? 'Super Fan' : entry.s.trend === 'trending' ? 'Fan Activo' : 'Fan',
        trophy,
      };
    });
}

/* ═══════════════════════════════════════════════════════════════════
   EVENT SCORES (mock)
   ═══════════════════════════════════════════════════════════════════ */
export const eventScores: RfwScoreData[] = [
  {
    entityId: 1, entityName: 'Festival de Bandas en Vivo 2026', entityType: 'radio',
    currentScore: 1850, weeklyScore: 320, monthlyScore: 750, allTimeScore: 1850,
    scoreChange: 85, rankingPosition: 1, rankingPreviousPosition: 2, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'comentarios', points: 400, count: 400 },
      { category: 'likes', points: 380, count: 380 },
      { category: 'compartidos', points: 250, count: 50 },
      { category: 'asistir_evento', points: 600, count: 30 },
      { category: 'menciones', points: 220, count: 110 },
    ],
    history: [
      { date: '2026-06-15', score: 1765, change: 70 }, { date: '2026-06-16', score: 1780, change: 15 },
      { date: '2026-06-17', score: 1800, change: 20 }, { date: '2026-06-18', score: 1820, change: 20 },
      { date: '2026-06-19', score: 1845, change: 25 }, { date: '2026-06-20', score: 1860, change: 15 },
      { date: '2026-06-21', score: 1850, change: -10 },
    ],
  },
  {
    entityId: 2, entityName: 'Noche Retro 80s y 90s', entityType: 'radio',
    currentScore: 1620, weeklyScore: 280, monthlyScore: 620, allTimeScore: 1620,
    scoreChange: 45, rankingPosition: 2, rankingPreviousPosition: 1, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'trending',
    breakdown: [
      { category: 'comentarios', points: 350, count: 350 },
      { category: 'likes', points: 340, count: 340 },
      { category: 'compartidos', points: 200, count: 40 },
      { category: 'asistir_evento', points: 500, count: 25 },
      { category: 'menciones', points: 230, count: 115 },
    ],
    history: [
      { date: '2026-06-15', score: 1575, change: 40 }, { date: '2026-06-16', score: 1585, change: 10 },
      { date: '2026-06-17', score: 1600, change: 15 }, { date: '2026-06-18', score: 1615, change: 15 },
      { date: '2026-06-19', score: 1625, change: 10 }, { date: '2026-06-20', score: 1630, change: 5 },
      { date: '2026-06-21', score: 1620, change: -10 },
    ],
  },
  {
    entityId: 3, entityName: 'Maratón Deportivo Total', entityType: 'radio',
    currentScore: 1480, weeklyScore: 310, monthlyScore: 580, allTimeScore: 1480,
    scoreChange: 120, rankingPosition: 3, rankingPreviousPosition: 5, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'comentarios', points: 320, count: 320 },
      { category: 'likes', points: 300, count: 300 },
      { category: 'compartidos', points: 180, count: 36 },
      { category: 'asistir_evento', points: 480, count: 24 },
      { category: 'menciones', points: 200, count: 100 },
    ],
    history: [
      { date: '2026-06-15', score: 1360, change: 100 }, { date: '2026-06-16', score: 1390, change: 30 },
      { date: '2026-06-17', score: 1410, change: 20 }, { date: '2026-06-18', score: 1440, change: 30 },
      { date: '2026-06-19', score: 1465, change: 25 }, { date: '2026-06-20', score: 1490, change: 25 },
      { date: '2026-06-21', score: 1480, change: -10 },
    ],
  },
  {
    entityId: 4, entityName: 'Concierto Acústico Solidario', entityType: 'radio',
    currentScore: 1320, weeklyScore: 190, monthlyScore: 480, allTimeScore: 1320,
    scoreChange: -30, rankingPosition: 4, rankingPreviousPosition: 3, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'active',
    breakdown: [
      { category: 'comentarios', points: 280, count: 280 },
      { category: 'likes', points: 260, count: 260 },
      { category: 'compartidos', points: 150, count: 30 },
      { category: 'asistir_evento', points: 400, count: 20 },
      { category: 'menciones', points: 230, count: 115 },
    ],
    history: [
      { date: '2026-06-15', score: 1350, change: -15 }, { date: '2026-06-16', score: 1340, change: -10 },
      { date: '2026-06-17', score: 1335, change: -5 }, { date: '2026-06-18', score: 1325, change: -10 },
      { date: '2026-06-19', score: 1330, change: 5 }, { date: '2026-06-20', score: 1325, change: -5 },
      { date: '2026-06-21', score: 1320, change: -5 },
    ],
  },
  {
    entityId: 5, entityName: 'Encuentro de Locutores 2026', entityType: 'radio',
    currentScore: 1150, weeklyScore: 160, monthlyScore: 420, allTimeScore: 1150,
    scoreChange: 55, rankingPosition: 5, rankingPreviousPosition: 6, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'comentarios', points: 240, count: 240 },
      { category: 'likes', points: 220, count: 220 },
      { category: 'compartidos', points: 120, count: 24 },
      { category: 'asistir_evento', points: 380, count: 19 },
      { category: 'menciones', points: 190, count: 95 },
    ],
    history: [
      { date: '2026-06-15', score: 1095, change: 50 }, { date: '2026-06-16', score: 1110, change: 15 },
      { date: '2026-06-17', score: 1125, change: 15 }, { date: '2026-06-18', score: 1135, change: 10 },
      { date: '2026-06-19', score: 1145, change: 10 }, { date: '2026-06-20', score: 1155, change: 10 },
      { date: '2026-06-21', score: 1150, change: -5 },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════
   CONTEST SCORES (mock)
   ═══════════════════════════════════════════════════════════════════ */
export const contestScores: RfwScoreData[] = [
  {
    entityId: 1, entityName: 'Concurso La Mejor Canción Original', entityType: 'radio',
    currentScore: 2100, weeklyScore: 380, monthlyScore: 880, allTimeScore: 2100,
    scoreChange: 140, rankingPosition: 1, rankingPreviousPosition: 1, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'trending',
    breakdown: [
      { category: 'participacion_concursos', points: 600, count: 120 },
      { category: 'comentarios', points: 450, count: 450 },
      { category: 'likes', points: 380, count: 380 },
      { category: 'compartidos', points: 300, count: 60 },
      { category: 'ganador_concurso', points: 200, count: 20 },
      { category: 'menciones', points: 170, count: 85 },
    ],
    history: [
      { date: '2026-06-15', score: 1960, change: 120 }, { date: '2026-06-16', score: 1990, change: 30 },
      { date: '2026-06-17', score: 2020, change: 30 }, { date: '2026-06-18', score: 2050, change: 30 },
      { date: '2026-06-19', score: 2080, change: 30 }, { date: '2026-06-20', score: 2110, change: 30 },
      { date: '2026-06-21', score: 2100, change: -10 },
    ],
  },
  {
    entityId: 2, entityName: 'Concurso Cover Virtual 2026', entityType: 'radio',
    currentScore: 1860, weeklyScore: 340, monthlyScore: 760, allTimeScore: 1860,
    scoreChange: -60, rankingPosition: 2, rankingPreviousPosition: 2, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'stable',
    breakdown: [
      { category: 'participacion_concursos', points: 550, count: 110 },
      { category: 'comentarios', points: 380, count: 380 },
      { category: 'likes', points: 320, count: 320 },
      { category: 'compartidos', points: 280, count: 56 },
      { category: 'ganador_concurso', points: 150, count: 15 },
      { category: 'menciones', points: 180, count: 90 },
    ],
    history: [
      { date: '2026-06-15', score: 1920, change: -30 }, { date: '2026-06-16', score: 1900, change: -20 },
      { date: '2026-06-17', score: 1890, change: -10 }, { date: '2026-06-18', score: 1875, change: -15 },
      { date: '2026-06-19', score: 1880, change: 5 }, { date: '2026-06-20', score: 1870, change: -10 },
      { date: '2026-06-21', score: 1860, change: -10 },
    ],
  },
  {
    entityId: 3, entityName: 'Concurso Locutor Revelación', entityType: 'radio',
    currentScore: 1640, weeklyScore: 420, monthlyScore: 680, allTimeScore: 1640,
    scoreChange: 210, rankingPosition: 3, rankingPreviousPosition: 5, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'participacion_concursos', points: 480, count: 96 },
      { category: 'comentarios', points: 320, count: 320 },
      { category: 'likes', points: 290, count: 290 },
      { category: 'compartidos', points: 240, count: 48 },
      { category: 'ganador_concurso', points: 100, count: 10 },
      { category: 'menciones', points: 210, count: 105 },
    ],
    history: [
      { date: '2026-06-15', score: 1430, change: 180 }, { date: '2026-06-16', score: 1480, change: 50 },
      { date: '2026-06-17', score: 1510, change: 30 }, { date: '2026-06-18', score: 1560, change: 50 },
      { date: '2026-06-19', score: 1610, change: 50 }, { date: '2026-06-20', score: 1650, change: 40 },
      { date: '2026-06-21', score: 1640, change: -10 },
    ],
  },
  {
    entityId: 4, entityName: 'Concurso Mejor Programa de Radio', entityType: 'radio',
    currentScore: 1420, weeklyScore: 230, monthlyScore: 540, allTimeScore: 1420,
    scoreChange: 65, rankingPosition: 4, rankingPreviousPosition: 4, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'growing',
    breakdown: [
      { category: 'participacion_concursos', points: 380, count: 76 },
      { category: 'comentarios', points: 280, count: 280 },
      { category: 'likes', points: 260, count: 260 },
      { category: 'compartidos', points: 200, count: 40 },
      { category: 'ganador_concurso', points: 80, count: 8 },
      { category: 'menciones', points: 220, count: 110 },
    ],
    history: [
      { date: '2026-06-15', score: 1355, change: 55 }, { date: '2026-06-16', score: 1370, change: 15 },
      { date: '2026-06-17', score: 1385, change: 15 }, { date: '2026-06-18', score: 1400, change: 15 },
      { date: '2026-06-19', score: 1415, change: 15 }, { date: '2026-06-20', score: 1425, change: 10 },
      { date: '2026-06-21', score: 1420, change: -5 },
    ],
  },
  {
    entityId: 5, entityName: 'Batalla de Freestyle RFW', entityType: 'radio',
    currentScore: 1280, weeklyScore: 360, monthlyScore: 520, allTimeScore: 1280,
    scoreChange: 180, rankingPosition: 5, rankingPreviousPosition: 7, lastScoreUpdate: '2026-06-21T08:00:00Z',
    trend: 'exploding',
    breakdown: [
      { category: 'participacion_concursos', points: 450, count: 90 },
      { category: 'comentarios', points: 260, count: 260 },
      { category: 'likes', points: 240, count: 240 },
      { category: 'compartidos', points: 160, count: 32 },
      { category: 'ganador_concurso', points: 60, count: 6 },
      { category: 'menciones', points: 110, count: 55 },
    ],
    history: [
      { date: '2026-06-15', score: 1100, change: 150 }, { date: '2026-06-16', score: 1140, change: 40 },
      { date: '2026-06-17', score: 1180, change: 40 }, { date: '2026-06-18', score: 1220, change: 40 },
      { date: '2026-06-19', score: 1260, change: 40 }, { date: '2026-06-20', score: 1290, change: 30 },
      { date: '2026-06-21', score: 1280, change: -10 },
    ],
  },
];

const eventThumbnails: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Live%20music%20festival%20stage%20with%20colorful%20stage%20lights%2C%20energetic%20crowd%20atmosphere%2C%20warm%20amber%20and%20gold%20lighting%2C%20concert%20venue%20with%20professional%20sound%20equipment%2C%20vibrant%20entertainment%20event%20photography%2C%20wide%20angle%20shot%20with%20dynamic%20energy&width=200&height=200&seq=event-thumb-1&orientation=squarish',
  2: 'https://readdy.ai/api/search-image?query=Retro%2080s%20nightclub%20party%20atmosphere%20with%20neon%20lights%2C%20disco%20ball%20reflections%2C%20vintage%20synthesizer%20and%20cassette%20tape%20aesthetic%2C%20warm%20pink%20and%20purple%20glow%2C%20nostalgic%20music%20event%2C%20cinematic%20mood%20lighting&width=200&height=200&seq=event-thumb-2&orientation=squarish',
  3: 'https://readdy.ai/api/search-image?query=Sports%20marathon%20broadcast%20event%20with%20dynamic%20motion%2C%20stadium%20atmosphere%20in%20background%2C%20energetic%20sports%20commentary%20setup%2C%20warm%20stadium%20floodlights%2C%20professional%20sports%20media%20production%2C%20exciting%20competition%20energy&width=200&height=200&seq=event-thumb-3&orientation=squarish',
  4: 'https://readdy.ai/api/search-image?query=Intimate%20acoustic%20concert%20charity%20event%2C%20warm%20candlelit%20atmosphere%2C%20acoustic%20guitar%20on%20stage%2C%20soft%20golden%20bokeh%20lights%2C%20heartfelt%20community%20gathering%2C%20warm%20and%20emotional%20photography%20style&width=200&height=200&seq=event-thumb-4&orientation=squarish',
  5: 'https://readdy.ai/api/search-image?query=Professional%20broadcasters%20networking%20conference%20event%2C%20modern%20studio%20setting%20with%20warm%20lighting%2C%20microphones%20and%20headsets%2C%20radio%20industry%20professionals%20gathering%2C%20warm%20golden%20hour%20ambiance%2C%20corporate%20event%20photography&width=200&height=200&seq=event-thumb-5&orientation=squarish',
};

const contestThumbnails: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Music%20songwriting%20competition%20award%20scene%2C%20golden%20microphone%20trophy%20on%20illuminated%20podium%2C%20warm%20spotlight%20effect%2C%20prestigious%20music%20contest%20atmosphere%2C%20elegant%20gold%20and%20amber%20tones%2C%20award%20ceremony%20photography&width=200&height=200&seq=contest-thumb-1&orientation=squarish',
  2: 'https://readdy.ai/api/search-image?query=Virtual%20music%20cover%20contest%20digital%20stage%2C%20singer%20performing%20with%20studio%20microphone%2C%20modern%20recording%20studio%20aesthetic%2C%20warm%20purple%20and%20gold%20lighting%2C%20online%20talent%20competition%2C%20contemporary%20music%20production&width=200&height=200&seq=contest-thumb-2&orientation=squarish',
  3: 'https://readdy.ai/api/search-image?query=Radio%20broadcaster%20talent%20search%20competition%2C%20young%20host%20speaking%20into%20professional%20microphone%2C%20warm%20studio%20lighting%20with%20red%20on-air%20sign%2C%20radio%20talent%20competition%20atmosphere%2C%20inspiring%20and%20motivational%20vibe&width=200&height=200&seq=contest-thumb-3&orientation=squarish',
  4: 'https://readdy.ai/api/search-image?query=Best%20radio%20program%20award%20ceremony%2C%20golden%20award%20plaque%20with%20radio%20wave%20design%2C%20elegant%20studio%20backdrop%20with%20warm%20lighting%2C%20prestigious%20broadcasting%20industry%20award%2C%20professional%20recognition%20event&width=200&height=200&seq=contest-thumb-4&orientation=squarish',
  5: 'https://readdy.ai/api/search-image?query=Freestyle%20rap%20battle%20competition%20with%20intense%20energy%2C%20two%20MCs%20facing%20off%20on%20stage%2C%20dynamic%20crowd%20reaction%2C%20warm%20amber%20stage%20lights%2C%20hip%20hop%20culture%20event%20photography%2C%20underground%20battle%20atmosphere%20with%20raw%20energy&width=200&height=200&seq=contest-thumb-5&orientation=squarish',
};

export function getEventRanking(timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  return eventScores
    .map((s) => {
      const score = getScoreForTime(s, timeFilter);
      return { s, score, scoreLabel: getScoreLabelForTime(s, timeFilter) };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry, i) => {
      const pos = i + 1;
      let trophy: DisplayRankingEntry['trophy'] = null;
      if (pos === 1) trophy = 'gold';
      else if (pos === 2) trophy = 'silver';
      else if (pos === 3) trophy = 'bronze';
      else if (pos <= 10) trophy = 'top10';

      return {
        position: pos,
        previousPosition: entry.s.rankingPreviousPosition,
        entityId: entry.s.entityId,
        entityName: entry.s.entityName,
        entitySubtitle: 'Evento RFW',
        logoUrl: eventThumbnails[entry.s.entityId] || '',
        score: entry.score,
        scoreLabel: entry.scoreLabel,
        scoreChange: entry.s.scoreChange,
        trend: entry.s.trend,
        highlight: 'Evento',
        trophy,
      };
    });
}

export function getContestRanking(timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  return contestScores
    .map((s) => {
      const score = getScoreForTime(s, timeFilter);
      return { s, score, scoreLabel: getScoreLabelForTime(s, timeFilter) };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry, i) => {
      const pos = i + 1;
      let trophy: DisplayRankingEntry['trophy'] = null;
      if (pos === 1) trophy = 'gold';
      else if (pos === 2) trophy = 'silver';
      else if (pos === 3) trophy = 'bronze';
      else if (pos <= 10) trophy = 'top10';

      return {
        position: pos,
        previousPosition: entry.s.rankingPreviousPosition,
        entityId: entry.s.entityId,
        entityName: entry.s.entityName,
        entitySubtitle: 'Concurso RFW',
        logoUrl: contestThumbnails[entry.s.entityId] || '',
        score: entry.score,
        scoreLabel: entry.scoreLabel,
        scoreChange: entry.s.scoreChange,
        trend: entry.s.trend,
        highlight: 'Concurso',
        trophy,
      };
    });
}

/* ═══════════════════════════════════════════════════════════════════
   MASTER RANKING DISPATCHER
   ═══════════════════════════════════════════════════════════════════ */
export function getRankingByType(entityType: RankingEntityType, timeFilter: TimeFilter = 'week'): DisplayRankingEntry[] {
  switch (entityType) {
    case 'radios': return getRadioRanking(timeFilter);
    case 'programas': return getProgramRanking(timeFilter);
    case 'locutores': return getHostRanking(timeFilter);
    case 'clubes': return getClubRanking(timeFilter);
    case 'fans': return getFanRanking(timeFilter);
    case 'eventos': return getEventRanking(timeFilter);
    case 'concursos': return getContestRanking(timeFilter);
    default: return [];
  }
}

export function getPodium(ranking: DisplayRankingEntry[]): { gold: DisplayRankingEntry | null; silver: DisplayRankingEntry | null; bronze: DisplayRankingEntry | null } {
  return {
    gold: ranking.length > 0 ? ranking[0] : null,
    silver: ranking.length > 1 ? ranking[1] : null,
    bronze: ranking.length > 2 ? ranking[2] : null,
  };
}

export function getRest(ranking: DisplayRankingEntry[]): DisplayRankingEntry[] {
  return ranking.slice(3);
}